import { Box } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'

type LogoShape = 'square' | 'wide' | 'tall'

type CompanyLogoProps = {
  src: string
  alt?: string
  maxHeight: number
  maxWidth: number
}

function getLogoShape(width: number, height: number): LogoShape {
  const ratio = width / height
  if (ratio >= 1.15) return 'wide'
  if (ratio <= 0.85) return 'tall'
  return 'square'
}

function getLogoContainerSize(
  shape: LogoShape,
  maxHeight: number,
  maxWidth: number,
  naturalWidth: number,
  naturalHeight: number
) {
  const ratio = naturalWidth / naturalHeight

  if (shape === 'wide') {
    return { width: maxWidth, height: maxHeight }
  }

  if (shape === 'tall') {
    const height = maxHeight
    const width = Math.min(maxWidth, Math.max(maxHeight * 0.5, Math.round(height * ratio)))
    return { width, height }
  }

  return { width: maxHeight, height: maxHeight }
}

const CompanyLogo = ({
  src,
  alt = 'Company logo',
  maxHeight,
  maxWidth,
}: CompanyLogoProps) => {
  const imgRef = useRef<HTMLImageElement>(null)
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(
    null
  )

  useEffect(() => {
    setNaturalSize(null)
    const img = imgRef.current
    if (img?.complete && img.naturalWidth > 0) {
      setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight })
    }
  }, [src])

  const containerSize = useMemo(() => {
    if (!naturalSize) {
      return { width: maxHeight, height: maxHeight }
    }

    const shape = getLogoShape(naturalSize.width, naturalSize.height)
    return getLogoContainerSize(
      shape,
      maxHeight,
      maxWidth,
      naturalSize.width,
      naturalSize.height
    )
  }, [maxHeight, maxWidth, naturalSize])

  return (
    <Box
      sx={{
        width: containerSize.width,
        height: containerSize.height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Box
        ref={imgRef}
        component="img"
        src={src}
        alt={alt}
        onLoad={(event) => {
          const img = event.currentTarget
          if (img.naturalWidth > 0) {
            setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight })
          }
        }}
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </Box>
  )
}

export default CompanyLogo
